import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Icon, InputCheckbox, InputText, Label, Text } from "~/components-shared";
import InputSelectControl from "~/components/InputSelectControl";
import lists from "~/lib/lists";
import api from "~/lib/api";
import { StatusStateEnum } from "~/types/enums";
import styled, { useTheme } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import manageRoleSelectors from "~/store/manageRoles/selectors";
import rolesActionCreators from "~/store/manageRoles/actions";
import InputToggleControl from "~/components/InputToggleControl";
const Fieldset = styled.fieldset`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const Authorities = styled.div`
  flex-grow: 1;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  min-height: 400px;
`;
const MemberForm = _ref => {
  let {
    control,
    watch,
    setValue
  } = _ref;
  const theme = useTheme();
  const {
    t
  } = useTranslation();
  const [authorities, setAuthorities] = useState([]);
  const roles = useSelector(manageRoleSelectors.getRoles);
  const roleStatus = useSelector(manageRoleSelectors.getStatus);
  let tempAuthorities = authorities;
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  useEffect(() => {
    if (roleStatus === StatusStateEnum.IDLE) {
      dispatch(rolesActionCreators.fetchStarted());
    }
  }, [dispatch, roleStatus]);
  if (search) {
    tempAuthorities = authorities.filter(authority => authority.includes(search));
  }
  const groupedAuthorities = tempAuthorities ? tempAuthorities.reduce((groups, authority) => {
    const prevGroups = groups;
    const [mainPart] = authority.split(".");
    const [levels] = mainPart.split(":");
    const [primary] = levels.split("/");
    if (!prevGroups[primary]) {
      prevGroups[primary] = [];
    }
    groups[primary].push(authority);
    return prevGroups;
  }, {}) : {};
  const handleChange = authority => {
    const selected = watch("authorities");
    if (selected.includes(authority)) {
      setValue("authorities", selected.filter(item => item !== authority));
    } else {
      setValue("authorities", [...selected, authority]);
    }
  };
  const handleChangeGroup = key => {
    const selected = watch("authorities");
    const filtered = selected.filter(item => !item.startsWith(key));
    const selectedAll = watch("authorities").sort().join().includes(groupedAuthorities[key].join());
    if (selectedAll) {
      setValue("authorities", filtered);
    } else {
      setValue("authorities", [...filtered, ...authorities.filter(item => item.startsWith(key))]);
    }
  };
  useEffect(() => {
    api.fetchManageCompanyAuthorities().then(response => {
      setAuthorities(response);
    });
  }, []);
  const applyAuthorities = roleId => {
    setValue("roleId", roleId);
    if (roles) {
      const roleAuthorities = roles.find(_ref2 => {
        let {
          id
        } = _ref2;
        return roleId === id;
      })?.authorities;
      if (roleAuthorities) {
        setValue("authorities", roleAuthorities);
      }
    }
  };
  return /*#__PURE__*/React.createElement(Fieldset, null, /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    gap: 5,
    equal: true
  }, /*#__PURE__*/React.createElement(InputSelectControl, {
    options: lists.members.statuses.names,
    name: "status",
    label: t("MemberForm.status"),
    s: {
      mb: 4
    },
    control: control,
    required: true
  }), /*#__PURE__*/React.createElement(InputSelectControl, {
    options: roles.map(_ref3 => {
      let {
        id,
        name
      } = _ref3;
      return {
        id,
        value: name
      };
    }),
    name: "roleId",
    label: t("MemberForm.role"),
    onChange: roleId => applyAuthorities(roleId),
    s: {
      mb: 4
    },
    control: control,
    required: true
  })), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    gap: 5,
    s: {
      mb: 4
    },
    equal: true
  }, /*#__PURE__*/React.createElement(InputSelectControl, {
    options: Object.keys(lists.members.department.names).map(key => ({
      id: key,
      value: lists.members.department.names[key]
    })),
    name: "department",
    label: t("ProfileBasicInfoForm.department"),
    control: control,
    maxWidth: "250px"
  }), /*#__PURE__*/React.createElement(InputSelectControl, {
    options: Object.keys(lists.members.level.names).map(key => ({
      id: key,
      value: lists.members.level.names[key]
    })),
    name: "level",
    label: t("ProfileBasicInfoForm.level"),
    control: control,
    maxWidth: "250px"
  })), /*#__PURE__*/React.createElement(InputToggleControl, {
    name: "pendingIntegrationChannels",
    label: t("MemberForm.pendingIntegrationChannels"),
    s: {
      mb: 3
    },
    control: control
  }), /*#__PURE__*/React.createElement(Label, {
    s: {
      mb: 2
    },
    required: true
  }, t("MemberForm.authorities")), /*#__PURE__*/React.createElement(InputText, {
    value: search,
    placeholder: t("MemberForm.search"),
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      color: theme.colors.text.secondary,
      src: "search",
      s: {
        mr: 2
      }
    }),
    color: "transparent",
    onChange: value => setSearch(value),
    s: {
      mb: 3
    },
    clearButton: true
  }), /*#__PURE__*/React.createElement(Authorities, null, Object.keys(groupedAuthorities).map(key => /*#__PURE__*/React.createElement(React.Fragment, {
    key: key
  }, /*#__PURE__*/React.createElement(InputCheckbox, {
    value: watch("authorities").sort().join().includes(groupedAuthorities[key].join()),
    onChange: () => handleChangeGroup(key),
    label: /*#__PURE__*/React.createElement(Text, {
      element: "h3"
    }, key)
  }), groupedAuthorities[key].map(authority => /*#__PURE__*/React.createElement(InputCheckbox, {
    key: authority,
    value: watch("authorities").includes(authority),
    onChange: () => handleChange(authority),
    label: authority,
    s: {
      ml: 5
    }
  }))))));
};
export default MemberForm;